import * as React from 'react';
import {FunctionComponent} from 'react';
import {classNames} from '../../../../Support/classNames';
import {formatSplitStrategy} from '../../../../Components/Utils/FormatSplitStrategy';
import {Distance} from '../../../../../Domain/Distance';
import {SplitStrategy, Strategy} from '../../../../../Business/calculateSplitTimes';
import {formatDistance} from '../../../../Support/Formatting/formatDistance';
import {Fraction} from '../../../../../Domain/Fraction';
import {InfoTooltip} from '../../../../Components/Utils/InfoTooltip';

interface OwnProps {
    mode: 'trend' | 'comparing';
    selectedSnippetIds: number[];
    splitStrategy: SplitStrategy;
    onResetSelectedSnippetIds: () => void;
    onChangeMode: (mode: 'trend' | 'comparing') => void;
    onChangeSplitStrategy: (splitStrategy: SplitStrategy) => void;
    className?: string;
}

export const CompareSelector: FunctionComponent<OwnProps> = ({
    mode,
    selectedSnippetIds,
    splitStrategy,
    onResetSelectedSnippetIds,
    onChangeSplitStrategy,
    onChangeMode,
    className,
}) => {
    if (mode === 'trend') {
        return (
            <div className={classNames(className, 'card')}>
                <div className="card-title">
                    <h5>Compare</h5>
                </div>
                <div className="card-body d-flex flex-column">
                    {selectedSnippetIds.length === 0 && <p>Select several workouts to compare them side by side.</p>}
                    {selectedSnippetIds.length === 1 && (
                        <p>Selected 1 workout, select another to compare them side by side.</p>
                    )}
                    {selectedSnippetIds.length > 1 && <p>Selected {selectedSnippetIds.length} workouts to compare.</p>}
                    <div className="row">
                        <div className="col-12 d-flex justify-content-between">
                            <button
                                className="btn btn-secondary btn-sm"
                                type="button"
                                disabled={selectedSnippetIds.length === 0}
                                onClick={() => {
                                    onChangeMode('comparing');
                                }}
                            >
                                Compare
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                type="button"
                                disabled={selectedSnippetIds.length === 0}
                                onClick={() => {
                                    onResetSelectedSnippetIds();
                                }}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classNames(className, 'card')}>
            <div className="card-title">
                <h5>Compare</h5>
            </div>
            <div className="card-body d-flex flex-column">
                <p>
                    Analyse workouts chunk by chunk by selecting a split.
                    <InfoTooltip
                        className="ms-2"
                        description="Some statistics are based on interpolated strokes, can differ from interval results"
                    />
                </p>
                <div className="row">
                    <div className="col-12 d-flex justify-content-between">
                        <div className="dropdown">
                            <button
                                className="btn btn-secondary btn-sm dropdown-toggle"
                                type="button"
                                id="splittablemode"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                {formatSplitStrategy(splitStrategy)}
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="splittablemode">
                                <li key={'fraction-' + 100}>
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            onChangeSplitStrategy({
                                                strategy: Strategy.Fraction,
                                                fraction: Fraction.fromPercentage(100),
                                            });
                                        }}
                                    >
                                        No split
                                    </button>
                                </li>
                                {[100, 200, 500, 1000, 2000]
                                    .map((distanceMeters) => Distance.fromMeters(distanceMeters))
                                    .map((distance) => {
                                        return (
                                            <li key={'distance-' + distance.meters}>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        onChangeSplitStrategy({
                                                            strategy: Strategy.Distance,
                                                            distance: distance,
                                                        });
                                                    }}
                                                >
                                                    {formatDistance(distance).formatted} {formatDistance(distance).unit}
                                                </button>
                                            </li>
                                        );
                                    })}
                                {[10, 20, 25, 100 / 3, 50]
                                    .map((percentage) => Fraction.fromPercentage(percentage))
                                    .map((fraction) => {
                                        return (
                                            <li key={'fraction-' + fraction.percentage}>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        onChangeSplitStrategy({
                                                            strategy: Strategy.Fraction,
                                                            fraction: fraction,
                                                        });
                                                    }}
                                                >
                                                    {fraction.percentage.toFixed(0) + ' %'}
                                                </button>
                                            </li>
                                        );
                                    })}
                            </ul>
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={() => onChangeMode('trend')}>
                            Back to report
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
